import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import useSeo from "../hooks/useSeo";
import MarketTicker from "../components/MarketTicker";
import {
  ArrowRight, Activity, Target, ShieldAlert, Sparkles, Layers,
  Workflow, Send, ShieldCheck, TrendingUp, Zap, BarChart3, Copy,
} from "lucide-react";

const ICONS = {
  Activity, Target, ShieldAlert, Sparkles, Layers, Workflow, Send, ShieldCheck, TrendingUp, Zap, Copy,
};

export default function Home() {
  const { t, lang } = useI18n();
  const features = t("features.list") || [];

  useSeo({
    title: lang === "fr"
      ? "Dr Trade Scalper Pro — Bot de Trading MT5 | Robot Expert Advisor MetaTrader 5"
      : "Dr Trade Scalper Pro — MT5 Trading Bot | Expert Advisor for MetaTrader 5",
    description: lang === "fr"
      ? "Bot de trading MT5 100 % automatisé pour scalping. 6 stratégies (STOKY, YAO, MCB, CRT, LOKI, THF Pulse), Smart SL, filtre multi-timeframe. Compatible Deriv, Weltrade, Exness, FBS. Indices synthétiques, Forex, crypto, métaux. Livraison 5-15 min."
      : "100% automated MT5 trading bot for scalping. 6 strategies (STOKY, YAO, MCB, CRT, LOKI, THF Pulse), Smart SL, multi-timeframe filter. Compatible with Deriv, Weltrade, Exness, FBS. Synthetic indices, Forex, crypto, metals. Delivery in 5-15 min.",
    keywords: lang === "fr"
      ? "bot de trading, robot de trading, EA MT5, Expert Advisor, MetaTrader 5, robot MT5, scalping MT5, robot forex, EA Deriv, bot Boom Crash, bot BreakX, robot FxVol, robot indices synthétiques, robot scalping automatique"
      : "trading bot, MT5 EA, Expert Advisor, MetaTrader 5 robot, scalping bot, forex robot, Deriv EA, Boom Crash bot, synthetic indices robot, automated trading",
    path: "/",
  });

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/15 blur-[120px] rounded-full pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-0 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-cyan font-mono px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
            {t("hero.tagline")}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter max-w-4xl mx-auto leading-[1.05]">
            {t("hero.title")}{" "}
            <span className="bg-gradient-to-r from-cyan-300 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              {t("hero.title_accent")}
            </span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            {t("hero.sub")}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/pricing" className="btn-primary px-6 py-3 rounded-lg text-sm inline-flex items-center gap-2" data-testid="hero-cta-pricing">
              {t("hero.cta_primary")} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/features" className="btn-ghost px-6 py-3 rounded-lg text-sm" data-testid="hero-cta-features">
              {t("hero.cta_secondary")}
            </Link>
          </div>

          {/* Stats line */}
          <div className="mt-16 grid grid-cols-3 gap-4 max-w-3xl mx-auto">
            {[
              { v: "6", l: t("hero.stat1"), i: <Workflow className="h-4 w-4" /> },
              { v: "15", l: t("hero.stat2"), i: <Sparkles className="h-4 w-4" /> },
              { v: "✓", l: t("hero.stat3"), i: <Send className="h-4 w-4" /> },
            ].map((s, i) => (
              <div key={i} className="glass rounded-xl px-4 py-5">
                <div className="flex items-center justify-center gap-2 text-cyan mb-1.5">
                  {s.i}
                  <div className="text-2xl sm:text-3xl font-bold font-mono">{s.v}</div>
                </div>
                <div className="text-xs sm:text-sm font-medium text-zinc-200 leading-tight tracking-tight">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Laptop showcase */}
        <div className="max-w-3xl mx-auto px-4 mt-2 -mb-2 sm:-mb-4" data-testid="hero-laptop">
          <img
            src="/assets/laptop_dtsp.png"
            alt="Dr Trade Scalper Pro running on MetaTrader 5"
            className="w-full h-auto select-none drop-shadow-[0_30px_60px_rgba(0,240,255,0.12)] block"
            loading="eager"
            draggable="false"
          />
        </div>

        {/* Live market ticker */}
        <MarketTicker />
      </section>

      {/* HIGHLIGHTED FEATURES */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="section-overline mb-3">{t("features.overline")}</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">{t("features.title")}</h2>
            <p className="text-zinc-400 text-base">{t("features.sub")}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Sélection robuste par icône — affiche Multi-stratégie, Filtre MTF, Intérêt composé, Risk Management */}
            {["Workflow", "Layers", "TrendingUp", "ShieldCheck"]
              .map((iconKey) => features.find((f) => f.i === iconKey))
              .filter(Boolean)
              .map((f, i) => {
                const Icon = ICONS[f.i] || Activity;
                return (
                  <div key={i} className="glass rounded-2xl p-6 hover:border-cyan-400/40 transition-colors" data-testid={`home-feature-${i}`}>
                    <div className="h-10 w-10 rounded-lg bg-cyan-400/10 grid place-items-center mb-4">
                      <Icon className="h-5 w-5 text-cyan" />
                    </div>
                    <div className="text-base font-semibold mb-1.5">{f.t}</div>
                    <div className="text-xs text-zinc-400 leading-relaxed">{f.d}</div>
                  </div>
                );
              })}
          </div>

          <div className="mt-10 text-center">
            <Link to="/features" className="text-sm text-cyan hover:text-cyan-300 inline-flex items-center gap-1.5">
              {t("common.learn_more")} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="glass rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/20 blur-[100px] rounded-full" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/15 blur-[100px] rounded-full" />
            <div className="relative">
              <h3 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
                {t("home.cta_strip.title_pre") || "Prêt à scalper avec une "}
                <span className="text-cyan">{t("home.cta_strip.title_accent") || "précision institutionnelle"}</span>
                {t("home.cta_strip.title_post") || " ?"}
              </h3>
              <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
                {t("home.cta_strip.lead") || "Une compilation personnalisée, livrée en 5–15 minutes, liée à votre numéro de compte MT5 quel que soit votre broker."}
              </p>
              <Link to="/pricing" className="btn-primary px-7 py-3.5 rounded-lg text-sm inline-flex items-center gap-2">
                {t("home.cta_strip.cta") || "Voir les tarifs"} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
